import { Link, useLocation, type LinkProps } from "@tanstack/react-router";
import {
  Activity,
  BriefcaseBusiness,
  Building2,
  CalendarCheck,
  ChevronDown,
  FileText,
  Heart,
  History,
  MessageSquareHeart,
  Flag,
  LayoutDashboard,
  LayoutGrid,
  ListChecks,
  Plug,
  Rocket,
  Settings,
  TrendingUp,
  UserRound,
  Users,
} from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { AskDrawer } from "./ask";
import { useAuth } from "../auth/auth";
import { useMe, useOrgMe } from "../api/hooks";
import { isPlatformAdminToken } from "../lib/portal";
import { usePortal } from "../lib/portal-context";
import { Chip } from "./ui";

type NavItem = { to: LinkProps["to"]; label: string; icon: ReactNode; planned?: boolean };
/** A run of items under one small heading; no heading for a standalone item.
 *  A headed section can be folded away by clicking its heading (the structure
 *  sheet's "click on and off to show / hide tabs underneath"). */
type NavSection = { heading?: string; items: NavItem[] };

const dashboard: NavItem = { to: "/", label: "Dashboard", icon: <LayoutDashboard size={12} /> };
const workforce: NavItem = { to: "/workforce", label: "Workforce", icon: <Activity size={12} /> };
const weeklyReport: NavItem = { to: "/report", label: "Weekly report", icon: <FileText size={12} /> };
const actions: NavItem = { to: "/actions", label: "Actions", icon: <ListChecks size={12} /> };
const oneOnOnes: NavItem = { to: "/one-on-ones", label: "1:1s", icon: <CalendarCheck size={12} /> };
const teamsAndPerformance: NavItem = { to: "/performance", label: "Teams and performance", icon: <TrendingUp size={12} /> };
const performance: NavItem = { ...teamsAndPerformance, label: "Performance" };
const goalsAndExecution: NavItem = { to: "/goals", label: "Goals and execution", icon: <Flag size={12} /> };
const goals: NavItem = { ...goalsAndExecution, label: "Goals" };
const work: NavItem = { to: "/work", label: "Work", icon: <BriefcaseBusiness size={12} /> };
const teamHealth: NavItem = { to: "/team", label: "Team health", icon: <Users size={12} /> };
const people: NavItem = { to: "/people", label: "People", icon: <UserRound size={12} /> };
// On the sheet but not built yet: shown greyed so the structure reads as designed.
const engagement: NavItem = { to: "/", label: "Engagement", icon: <Heart size={12} />, planned: true };
const impactHistory: NavItem = { to: "/impact", label: "Impact history", icon: <History size={12} /> };
const managementOverview: NavItem = { to: "/management", label: "Management overview", icon: <LayoutGrid size={12} /> };
const coaching: NavItem = { to: "/", label: "Coaching", icon: <MessageSquareHeart size={12} />, planned: true };

/** The organisation admin's portal, laid out as the combined-SKU structure sheet has it:
 *  Dashboard, then the two product halves, with the tabs not yet built marked as planned.
 *  The plumbing (set up, organisation, integrations, settings) lives in the user menu. */
const admin: NavSection[] = [
  { items: [dashboard] },
  { heading: "Workforce intelligence", items: [workforce, actions, engagement, impactHistory, weeklyReport] },
  { heading: "Management", items: [managementOverview, teamsAndPerformance, goalsAndExecution, oneOnOnes, coaching] },
];
const adminMenu: NavItem[] = [
  { to: "/setup", label: "Set up", icon: <Rocket size={12} /> },
  { to: "/organisation", label: "Organisation", icon: <Building2 size={12} /> },
  { to: "/connections", label: "Integrations", icon: <Plug size={12} /> },
  { to: "/settings", label: "Settings", icon: <Settings size={12} /> },
];

/** An admin who also manages people gets the two team pages, under their own heading. */
const myTeam: NavSection = { heading: "My team", items: [teamHealth, { ...people, label: "Team members" }] };

/** The manager portal from the same sheet: Team health is the dashboard, then the two halves. */
const manager: NavSection[] = [
  { items: [teamHealth] },
  { heading: "Workforce intelligence", items: [people, actions, oneOnOnes] },
  { heading: "Management", items: [coaching, performance, goals, work] },
];
const managerMenu: NavItem[] = [{ to: "/settings", label: "Settings", icon: <Settings size={12} /> }];

/** The Humindly admin portal (MVP-G2/G3): one entry for now, more as Epic G lands. */
const platform: NavSection[] = [
  { items: [{ to: "/admin", label: "Organisations", icon: <LayoutGrid size={12} /> }] },
];

const FOLDED_KEY = "nav.folded";
function readFolded(): string[] {
  try {
    const raw = localStorage.getItem(FOLDED_KEY);
    const v: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}
function writeFolded(v: string[]) {
  try {
    localStorage.setItem(FOLDED_KEY, JSON.stringify(v));
  } catch {
    // Private window or blocked storage: the fold just doesn't survive a reload.
  }
}

function NavLink({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) {
  if (item.planned) {
    return (
      <div className="app-nav opacity-50 cursor-default" aria-disabled title="On the roadmap; not built yet">
        <span className="ic">{item.icon}</span>
        <span className="flex-1 truncate">{item.label}</span>
        <Chip tone="neutral">Planned</Chip>
      </div>
    );
  }
  return (
    <Link
      to={item.to}
      className="app-nav"
      activeProps={{ className: "app-nav active" }}
      activeOptions={{ exact: item.to === "/" }}
      onClick={onNavigate}
    >
      <span className="ic">{item.icon}</span>
      {item.label}
    </Link>
  );
}

function Section({ section, first }: { section: NavSection; first: boolean }) {
  const [folded, setFolded] = useState(() => (section.heading ? readFolded().includes(section.heading) : false));
  const pathname = useLocation().pathname;
  // A folded section still shows the page you are on, so the active item never vanishes.
  const visible = folded ? section.items.filter((i) => !i.planned && (i.to === "/" ? pathname === "/" : pathname.startsWith(String(i.to)))) : section.items;
  const toggle = () => {
    if (!section.heading) return;
    const next = !folded;
    setFolded(next);
    const set = new Set(readFolded());
    if (next) set.add(section.heading);
    else set.delete(section.heading);
    writeFolded([...set]);
  };
  return (
    <div className={`flex flex-col gap-1.5 ${first ? "" : "mt-3"}`}>
      {section.heading && (
        <button type="button" className="nav-heading flex items-center justify-between w-full cursor-pointer" onClick={toggle} aria-expanded={!folded}>
          <span>{section.heading}</span>
          <ChevronDown size={11} className={`transition-transform ${folded ? "-rotate-90" : ""}`} aria-hidden />
        </button>
      )}
      {visible.map((i) => (
        <NavLink key={i.label} item={i} />
      ))}
    </div>
  );
}

/** Bottom-left user menu: who you are, the platform pages, sign out. */
function UserMenu({ email, items, onLogout }: { email: string; items: NavItem[]; onLogout: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  const initial = (email.trim()[0] ?? "·").toUpperCase();
  return (
    <div ref={ref} className="relative mt-3">
      {open && (
        <div className="card absolute bottom-full left-0 right-0 mb-2 !p-2 flex flex-col gap-1 shadow-[var(--shadow-btn)]" role="menu">
          {items.map((i) => (
            <NavLink key={i.label} item={i} onNavigate={() => setOpen(false)} />
          ))}
          <button type="button" className="app-nav text-left border-t border-line rounded-none mt-1 pt-3" onClick={onLogout} role="menuitem">
            <span className="ic">→</span>
            Sign out
          </button>
        </div>
      )}
      <button
        type="button"
        className={`app-nav w-full text-left ${open ? "active" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={email}
      >
        <span className="ic">{initial}</span>
        <span className="truncate flex-1 text-[12.5px]">{email}</span>
        <ChevronDown size={12} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
      </button>
    </div>
  );
}

export function Shell({ children }: { children: ReactNode }) {
  const { logout, tokens } = useAuth();
  const me = useMe();
  const isAdmin = me.data?.roles.some((r) => r === "hr_admin" || r === "platform_admin") ?? false;
  // From the token, like the root gate, so the switch link never appears a frame late.
  const isPlatform = isPlatformAdminToken(tokens?.id_token);
  const { setPortal } = usePortal();
  const inAdminPortal = useLocation().pathname.startsWith("/admin");
  const org = useOrgMe();
  const managesPeople = (org.data?.direct_reports.length ?? 0) > 0;
  const sections = inAdminPortal ? platform : isAdmin ? (managesPeople ? [admin[0], myTeam, ...admin.slice(1)] : admin) : manager;
  const menu = inAdminPortal ? [] : isAdmin ? adminMenu : managerMenu;
  const [askOpen, setAskOpen] = useState(false);
  return (
    <div className="grid min-h-screen print:block" style={{ gridTemplateColumns: "220px 1fr" }}>
      <aside className="bg-white border-r border-line px-4 py-[22px] flex flex-col gap-1.5 sticky top-0 h-screen print:hidden">
        <img src="/humindly-logo.png" alt="Humindly" className="h-[26px] w-auto self-start mx-1.5 mb-[18px]" />
        {sections.map((section, n) => (
          <Section key={section.heading ?? n} section={section} first={n === 0} />
        ))}
        {isPlatform && (
          <Link
            to={inAdminPortal ? "/" : "/admin"}
            className="app-nav mt-2 border-t border-line pt-3 rounded-none"
            onClick={() => setPortal(inAdminPortal ? "org" : "admin")}
          >
            <span className="ic">⇄</span>
            {inAdminPortal ? "My organisation" : "Humindly admin"}
          </Link>
        )}
        {!inAdminPortal && (
          <div className="ai mt-auto">
            <b>✦ Humindly AI</b>
            <span>Ask a question about your workforce, get instant insights.</span>
            <button className="btn btn-light w-full !py-2 !text-[12.5px]" onClick={() => setAskOpen(true)}>
              Ask Humindly AI ›
            </button>
          </div>
        )}
        <div className={inAdminPortal ? "mt-auto" : ""}>
          <UserMenu email={me.data?.email ?? "…"} items={menu} onLogout={logout} />
        </div>
      </aside>
      <main className="min-w-0">{children}</main>
      <AskDrawer open={askOpen} onClose={() => setAskOpen(false)} />
    </div>
  );
}
